// ChildHistory.jsx
import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortUp, faSortDown, faFile, faDownload } from '@fortawesome/free-solid-svg-icons';
import { useDocument } from './hooks/useDocument';
import { STATUS_CONFIG, getStatusConfig, formatDate } from './utils/statusConfig';

const ChildHistory = ({ childData, mode }) => {
  const historyData = childData.history || [];
  const { getDocumentInfo, downloadDocumentFile, loading: docLoading } = useDocument();
  const [expandedDocs, setExpandedDocs] = useState({});
  const [expandedItems, setExpandedItems] = useState({});

  const columns = [
    { key: 'date', label: 'Дата', sortable: true, width: '15%' },
    { key: 'action', label: 'Подія', sortable: true, width: '25%' },
    { key: 'state', label: 'Статус', sortable: true, width: '20%' },
    { key: 'details', label: 'Деталі', sortable: false, width: '30%' },
    { key: 'user', label: 'Користувач', sortable: true, width: '10%' }
  ];

  const [sortConfig, setSortConfig] = React.useState({ key: 'date', direction: 'desc' });

  const handleSort = (key) => {
    let direction = 'desc';
    if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setSortConfig({ key, direction });
  };

  const sortedData = [...historyData].sort((a, b) => {
    if (sortConfig.key === 'date') {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      
      if (dateA < dateB) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (dateA > dateB) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    }
    
    if (a[sortConfig.key] < b[sortConfig.key]) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (a[sortConfig.key] > b[sortConfig.key]) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  const getSortIcon = (columnKey) => {
    if (columnKey !== sortConfig.key) return faSort;
    return sortConfig.direction === 'asc' ? faSortUp : faSortDown;
  };

  // Обработчик клика по документу
  const handleDocumentClick = async (docId, historyItemId) => {
    try {
      const docInfo = await getDocumentInfo(docId);
      
      setExpandedDocs(prev => ({
        ...prev,
        [historyItemId]: {
          docId,
          files: docInfo.files || [],
          name: docInfo.name,
          description: docInfo.description
        }
      }));
    } catch (error) {
      console.error('Error loading document info:', error);
    }
  };

  // Скачивание файла документа
  const handleDownloadFile = async (docId, fileId, fileName) => {
    try {
      await downloadDocumentFile(docId, fileId, fileName);
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  // Переключение расширенного просмотра элемента
  const toggleExpandedItem = (itemId) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  // Закрытие просмотра документов
  const closeDocumentView = (historyItemId) => {
    setExpandedDocs(prev => {
      const newState = { ...prev };
      delete newState[historyItemId];
      return newState;
    });
  };

  return (
    <div className="child-history">
      <div className="table-responsive">
        <table className="history-table">
          <thead>
            <tr>
              {columns.map((column) => (
                <th 
                  key={column.key}
                  style={{ width: column.width }}
                  onClick={() => column.sortable && handleSort(column.key)}
                  className={column.sortable ? 'sortable' : ''}
                >
                  <div className="d-flex align-items-center">
                    {column.label}
                    {column.sortable && (
                      <FontAwesomeIcon 
                        icon={getSortIcon(column.key)} 
                        className="ms-1" 
                        style={{ fontSize: '12px' }}
                      />
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedData.map((item) => {
              const config = getStatusConfig(item);
              const isExpanded = expandedItems[item.id];
              const docExpanded = expandedDocs[item.id];
              
              return (
                <React.Fragment key={item.id}>
                  <tr className="history-item">
                    <td>
                      <div className="history-date">{config.date}</div>
                    </td>
                    <td>
                      <div className="d-flex align-items-center">
                        <span className="action-icon me-2">{config.action.icon}</span>
                        <div>
                          <div className="action-label">{config.action.label}</div>
                          {item.kindergarten && (
                            <small className="text-muted">{item.kindergarten}</small>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`status-badge ${config.state.badgeClass}`}>
                        {config.state.icon} {config.state.label}
                      </span>
                    </td>
                    <td>
                      <div className="action-description">
                        {config.action.description(item)}
                      </div>
                      
                      {/* Документы */}
                      {config.documents.length > 0 && (
                        <div className="document-buttons mt-2">
                          {config.documents.map(doc => (
                            <button
                              key={`${item.id}-${doc.id}`}
                              className="btn btn-sm btn-outline-primary me-2 mb-1"
                              onClick={() => handleDocumentClick(doc.id, item.id)}
                              disabled={docLoading}
                            >
                              <FontAwesomeIcon icon={faFile} className="me-1" />
                              {doc.label}
                            </button>
                          ))}
                        </div>
                      )}
                      
                      {/* Дополнительная информация */}
                      {(item.comment || config.documents.length > 0) && (
                        <button
                          className="btn btn-sm btn-link p-0 mt-1"
                          onClick={() => toggleExpandedItem(item.id)}
                        >
                          {isExpanded ? 'Менше' : 'Більше'}...
                        </button>
                      )}
                    </td>
                    <td>
                      <div className="user-info">
                        {config.user}
                      </div>
                    </td>
                  </tr>
                  
                  {/* Расширенная информация */}
                  {isExpanded && (
                    <tr className="expanded-info-row">
                      <td colSpan={5}>
                        <div className="expanded-content">
                          {item.comment && (
                            <div className="comment-section mb-3">
                              <strong>Коментар:</strong>
                              <p className="mb-0 mt-1">{item.comment}</p>
                            </div>
                          )}
                          
                          {item.group && (
                            <div className="group-info mb-2">
                              <strong>Група:</strong> {item.group}
                            </div>
                          )}
                          
                          {item.details && (
                            <div className="details-info">
                              <strong>Деталі:</strong> {item.details}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                  
                  {/* Блок с документами */}
                  {docExpanded && (
                    <tr className="document-expanded-row">
                      <td colSpan={5}>
                        <div className="document-viewer">
                          <div className="document-header">
                            <h6>{docExpanded.name}</h6>
                            <button
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => closeDocumentView(item.id)}
                            >
                              ×
                            </button>
                          </div>
                          
                          {docExpanded.description && (
                            <p className="document-description">
                              {docExpanded.description}
                            </p>
                          )}
                          
                          <div className="document-files">
                            <h6>Файли документа:</h6>
                            {docExpanded.files.length > 0 ? (
                              <div className="files-list">
                                {docExpanded.files.map(file => (
                                  <div key={file._id} className="file-item">
                                    <div className="file-info">
                                      <FontAwesomeIcon icon={faFile} className="me-2" />
                                      <span className="file-name">{file.name}</span>
                                      <span className="file-size">
                                        ({formatFileSize(file.length)})
                                      </span>
                                      <span className="file-date">
                                        {formatDate(file.date_upload)}
                                      </span>
                                    </div>
                                    <button
                                      className="btn btn-sm btn-outline-success"
                                      onClick={() => 
                                        handleDownloadFile(
                                          docExpanded.docId, 
                                          file._id, 
                                          file.name
                                        )
                                      }
                                      disabled={docLoading}
                                    >
                                      <FontAwesomeIcon icon={faDownload} />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-muted">Файли відсутні</p>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {sortedData.length === 0 && (
        <div className="no-data">
          Історія відсутня
        </div>
      )}
    </div>
  );
};

// Вспомогательная функция для форматирования размера файла
const formatFileSize = (bytes) => {
  if (!bytes) return '0 Б';
  const sizes = ['Б', 'КБ', 'МБ', 'ГБ'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
};

export default ChildHistory;